import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bookmark, GitCompare, Mail, Search, Star, Sparkles, BellRing, Trash2, ArrowRight, Inbox,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ScholarshipAlert from "../components/ScholarshipAlert";
import { useCompare } from "../context/CompareContext";
import { api } from "../lib/api";

const read = (k) => { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } };

export default function SavedDashboard() {
  const navigate = useNavigate();
  const { items, remove } = useCompare();
  const [quiz, setQuiz] = useState(null);
  const [finder, setFinder] = useState(null);
  const [alert, setAlert] = useState(null);
  const [email, setEmail] = useState("");
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setQuiz(read("cc_quiz"));
    setFinder(read("cc_finder"));
    const a = read("cc_alert");
    setAlert(a);
    if (a?.email) setEmail(a.email);
  }, []);

  const lookup = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const r = await api.get(`/matches/by-email`, { params: { email } });
      setMatches(r.data);
    } catch { setMatches([]); } finally { setLoading(false); }
  };

  const Section = ({ icon: Icon, title, children, action }) => (
    <div className="bg-white rounded-2xl cc-shadow-card p-6 md:p-7">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-head text-xl font-bold text-[#16244F] flex items-center gap-2"><Icon size={20} className="text-[#2b8cf0]" /> {title}</h2>
        {action}
      </div>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f6f8fc] flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="bg-[#16244F] py-12 md:py-14">
          <div className="max-w-[1100px] mx-auto px-5">
            <h1 className="font-head text-3xl md:text-4xl font-bold text-white flex items-center gap-2"><Bookmark size={28} className="text-[#F9B57C]" /> My Saved Items</h1>
            <p className="text-white/75 mt-3">Everything you've shortlisted, matched and subscribed to, in one place.</p>
          </div>
        </section>

        <section className="max-w-[1100px] mx-auto px-5 py-10 md:py-12 space-y-7">
          {/* Compare shortlist */}
          <Section icon={GitCompare} title={`Compare Shortlist (${items.length})`}
            action={items.length >= 2 && <button onClick={() => navigate("/compare")} className="text-sm font-head font-semibold text-[#F58220] inline-flex items-center gap-1">Compare <ArrowRight size={14} /></button>}>
            {items.length === 0 ? (
              <EmptyRow text="No programs shortlisted yet." cta="Browse Course Finder" onClick={() => navigate("/course-finder")} />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {items.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100">
                    <img src={c.image} alt="" className="w-14 h-14 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="font-head font-semibold text-[#16244F] text-sm truncate">{c.course}</div>
                      <div className="text-xs text-[#8a90a0] truncate">{c.university}</div>
                    </div>
                    <button onClick={() => remove(c.id)} className="text-gray-300 hover:text-red-500"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Quiz + Finder */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
            <Section icon={Sparkles} title="Career Quiz Result">
              {quiz?.result ? (
                <div>
                  <div className="text-sm text-[#5a6274]">Your strongest match</div>
                  <div className="font-head text-2xl font-bold text-[#2b8cf0] mt-1">{quiz.result}</div>
                  <button onClick={() => navigate("/assessment")} className="mt-4 text-sm font-head font-semibold text-[#F58220]">Retake quiz</button>
                </div>
              ) : <EmptyRow text="You haven't taken the quiz yet." cta="Take the quiz" onClick={() => navigate("/assessment")} />}
            </Section>

            <Section icon={Star} title="Last Course Matches">
              {finder?.items?.length ? (
                <div className="space-y-2">
                  {finder.items.slice(0, 3).map((m) => (
                    <div key={m.id} className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-head font-semibold text-[#16244F] text-sm truncate">{m.course}</div>
                        <div className="text-xs text-[#8a90a0] truncate">{m.university}</div>
                      </div>
                      <span className="text-xs font-bold text-[#F58220] shrink-0">{m.fit}% fit</span>
                    </div>
                  ))}
                  <button onClick={() => navigate("/course-finder")} className="mt-2 text-sm font-head font-semibold text-[#F58220]">Run finder again</button>
                </div>
              ) : <EmptyRow text="No matches saved yet." cta="Find courses" onClick={() => navigate("/course-finder")} />}
            </Section>
          </div>

          {/* Emailed matches lookup */}
          <Section icon={Inbox} title="Emailed Matches">
            <form onSubmit={lookup} className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a90a0]" />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email to load saved matches" className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] outline-none" />
              </div>
              <button type="submit" className="inline-flex items-center justify-center gap-2 bg-[#16244F] hover:bg-[#223464] text-white font-head font-semibold px-5 py-3 rounded-lg transition-colors"><Search size={16} /> Load</button>
            </form>
            {loading ? <div className="text-[#5a6274] text-sm">Loading...</div>
              : matches == null ? <div className="text-[#8a90a0] text-sm">Enter the email you used to email yourself matches.</div>
              : matches.length === 0 ? <div className="text-[#8a90a0] text-sm">No emailed matches found for this address.</div>
              : (
                <div className="space-y-4">
                  {matches.map((m) => (
                    <div key={m.id} className="rounded-xl border border-gray-100 p-4">
                      <div className="text-xs text-[#8a90a0] mb-2">{new Date(m.created_at).toLocaleString("en-IN")} · {Object.values(m.criteria || {}).filter(Boolean).join(" · ")}</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {m.items.map((it, i) => (
                          <div key={i} className="flex items-center justify-between gap-2 text-sm">
                            <span className="text-[#16244F] font-medium truncate">{it.course}</span>
                            <span className="text-[#F58220] font-bold shrink-0">{it.fit}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </Section>

          {/* Scholarship alerts */}
          {alert ? (
            <Section icon={BellRing} title="Scholarship Alerts">
              <div className="flex items-center gap-3 text-[#5a6274]">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#16a34a]/10 text-[#16a34a] text-sm font-semibold"><BellRing size={15} /> Active</span>
                <span className="text-sm">Alerts for {[alert.field, alert.level, alert.country].filter((x) => x && x !== "Any").join(" · ") || "all programmes"} → {alert.email}</span>
              </div>
            </Section>
          ) : (
            <ScholarshipAlert defaults={finder?.criteria || {}} />
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

function EmptyRow({ text, cta, onClick }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[#8a90a0] text-sm">{text}</span>
      <button onClick={onClick} className="text-sm font-head font-semibold text-[#F58220] inline-flex items-center gap-1">{cta} <ArrowRight size={14} /></button>
    </div>
  );
}
